import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrash, faStar, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import Accordion from '../NavTab/Accordion';
import Table from '../Listing/Table';
import Button from '../Form/Button';
import PatientAddressModal from '../Modal/PatientAddressModal';
import ConfirmModal from '../Modal/ConfirmModal';
import { eventBus } from '../../bus/eventBus';
import './PatientAddresses.scss';
import { API_BASE_URL } from '../../api';

const PatientAddresses = () => {
    const { id } = useParams();
    const { t } = useTranslation();
    const [addresses, setAddresses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingAddress, setEditingAddress] = useState(null);
    const [keyword, setKeyword] = useState('');
    const [deleteIds, setDeleteIds] = useState([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const fetchAddresses = useCallback(() => {
        setLoading(true);
        axios.get(`${API_BASE_URL}/api/v1/patients/${id}/addresses`, { withCredentials: true })
            .then(res => setAddresses(res.data))
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    }, [id]);

    useEffect(() => {
        fetchAddresses();
    }, [fetchAddresses]);

    useEffect(() => {
        eventBus.on('patientAddressesUpdated', fetchAddresses);
        return () => eventBus.off('patientAddressesUpdated', fetchAddresses);
    }, [fetchAddresses]);

    const handleEdit = (address) => {
        setEditingAddress(address);
        setShowModal(true);
    };

    const handleDelete = (addressId) => {
        axios.delete(`${API_BASE_URL}/api/v1/patients/${id}/addresses/${addressId}`, { withCredentials: true })
            .then(() => {
                toast.success('Adresse supprimée avec succès !');
                fetchAddresses();
            })
            .catch(() => toast.error('Erreur lors de la suppression de l\'adresse'));
    };

    const handleBulkDelete = (ids) => {
        setDeleteIds(ids);
        setShowDeleteConfirm(true);
    };

    const handleConfirmDelete = async () => {
        try {
            await Promise.all(
                deleteIds.map(addressId =>
                    axios.delete(`${API_BASE_URL}/api/v1/patients/${id}/addresses/${addressId}`, { withCredentials: true })
                )
            );
            toast.success(deleteIds.length > 1 ? `${deleteIds.length} adresses supprimées` : 'Adresse supprimée');
            fetchAddresses();
        } catch {
            toast.error('Erreur lors de la suppression');
        } finally {
            setShowDeleteConfirm(false);
            setDeleteIds([]);
        }
    };

    const columns = [
        {
            Header: 'Adresse',
            accessor: 'addressLine1',
            sortable: true,
            className: 'pa-col-address',
            Cell: (row) => (
                <div>
                    <div className="fw-semibold">{row.addressLine1}</div>
                    {row.addressLine2 && <div className="text-muted small">{row.addressLine2}</div>}
                </div>
            ),
        },
        { Header: 'Ville', accessor: 'city', sortable: true },
        { Header: 'Code postal', accessor: 'postalCode', sortable: true },
        { Header: 'Pays', accessor: 'country', sortable: true },
        {
            Header: 'Par défaut',
            accessor: 'default',
            Cell: (row) => row.default ? (
                <span
                    className="pa-default-star"
                    data-tooltip-id="tooltip"
                    data-tooltip-content="Adresse par défaut"
                >
                    <FontAwesomeIcon icon={faStar} />
                </span>
            ) : null,
        },
        {
            Header: '',
            accessor: 'actions',
            className: 'text-end',
            Cell: (row) => (
                <div className="pa-card-actions">
                    <button type="button" className="pa-action-btn" title="Modifier" onClick={() => handleEdit(row)}>
                        <FontAwesomeIcon icon={faPen} />
                    </button>
                    <Button
                        className="pa-action-btn pa-action-btn--danger"
                        onConfirm={() => handleDelete(row.id)}
                        title="Supprimer"
                    >
                        <FontAwesomeIcon icon={faTrash} />
                    </Button>
                </div>
            ),
        },
    ];

    // Card-view design for this listing (Table's enableCardView feature, forced on mobile)
    // — reuses the actions column's own buttons so both views offer identical actions.
    const addressActionsColumn = columns.find((c) => c.accessor === 'actions');
    const renderAddressCard = (row) => (
        <div className="card h-100 shadow-sm">
            <div className="card-body d-flex flex-column" style={{ padding: 14, gap: 6 }}>
                <div className="d-flex align-items-start justify-content-between">
                    <div>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: '#111827' }}>{row.addressLine1}</div>
                        {row.addressLine2 && <div style={{ fontSize: 11.5, color: '#9ca3af' }}>{row.addressLine2}</div>}
                    </div>
                    {row.default && (
                        <span
                            className="pa-default-star"
                            data-tooltip-id="tooltip"
                            data-tooltip-content="Adresse par défaut"
                        >
                            <FontAwesomeIcon icon={faStar} />
                        </span>
                    )}
                </div>

                {(row.city || row.postalCode || row.country) && (
                    <span style={{ fontSize: 11.5, color: '#6b7280' }}>
                        {[row.postalCode, row.city, row.country].filter(Boolean).join(' · ')}
                    </span>
                )}

                <div className="d-flex align-items-center justify-content-end" style={{ marginTop: 'auto', paddingTop: 6 }}>
                    {addressActionsColumn?.Cell(row)}
                </div>
            </div>
        </div>
    );

    const selectionActions = [
        {
            label: 'Supprimer',
            icon: faTrash,
            variant: 'danger',
            onClick: handleBulkDelete,
        },
    ];

    return (
        <div className="pa">
            <Accordion
                id="patientAddresses"
                parent="patient_information_container"
                title={t('Adresses')}
                icon={faMapMarkerAlt}
                flush
            >
                <Table
                    mode="client"
                    columns={columns}
                    data={addresses}
                    loading={loading}
                    keyword={keyword}
                    setKeyword={setKeyword}
                    emptyMessage="Aucune adresse"
                    searchPlaceholder="Rechercher une adresse..."
                    showColumnsButton={false}
                    showExportButton={false}
                    multiSelect={true}
                    selectionActions={selectionActions}
                    enableCardView={true}
                    renderCard={renderAddressCard}
                />
            </Accordion>

            <PatientAddressModal
                show={showModal}
                onClose={() => setShowModal(false)}
                patientId={id}
                address={editingAddress}
                forceDefault={addresses.length <= 1}
                onSuccess={() => {
                    setShowModal(false);
                    fetchAddresses();
                }}
            />

            <ConfirmModal
                show={showDeleteConfirm}
                title={deleteIds.length > 1 ? `Supprimer ${deleteIds.length} adresses` : 'Supprimer l\'adresse'}
                description={deleteIds.length > 1
                    ? `Êtes-vous sûr de vouloir supprimer ces ${deleteIds.length} adresses ? Cette action est irréversible.`
                    : "Êtes-vous sûr de vouloir supprimer cette adresse ? Cette action est irréversible."
                }
                confirmLabel={deleteIds.length > 1 ? `Supprimer les ${deleteIds.length} adresses` : 'Supprimer'}
                cancelLabel="Annuler"
                confirmVariant="danger"
                onClose={() => { setShowDeleteConfirm(false); setDeleteIds([]); }}
                onConfirm={handleConfirmDelete}
            />
        </div>
    );
};

export default PatientAddresses;
